import { cloneDeep } from 'lodash';

import {
  SnippetyChartData,
  SnippetyResponseWithChart,
  SnippetyResponseWithImage,
  SnippetyResponseWithVideo,
  VideoDataForSummarian
} from 'hooks/useSnippetyQuery';
import { TIMES } from 'utils/time';

export type MediaTypes = 'image' | 'video' | 'chart' | 'iframe' | 'url' | '';

export const IMAGE_MANAGER_ID_PATTERN = /^im-\d+$/;

// Supports:
// - cdc_ + 24 hex characters (e.g., cdc_1234567890abcdef12345678)
// - 24 hex characters (e.g., 1234567890abcdef12345678)
// - Full chart tool url
// - dynamic inset url
// - embed html
export const CHART_ID_PATTERN =
  /^(?<domain>https?:\/\/asset(?:\.stg)?\.wsj\.net\/dynamic-insets\/charts\/|https:\/\/charts\.((dev|int)\.)?(dowjones\.io|djtools\.dowjones)\/(?<property>.*?)\/chart\/)?(cdc_)?(?<id>[0-9A-Fa-f]{24})(\.json|_embed\.html|\.html)?$/;

export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[4][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const errorMessages = {
  image: {
    invalidInput: 'You must enter an image ID',
    invalidId: 'Invalid entry, please enter a valid image ID',
    serverError: 'An error has occurred while trying to fetch the image data'
  },
  video: {
    invalidInput: 'You must enter a video GUID',
    invalidId: 'Invalid entry, please enter a valid video GUID',
    serverError: 'An error has occurred while trying to fetch the video data'
  },
  chart: {
    invalidInput: 'You must enter a chart ID or dynamic inset URL',
    invalidId: 'Invalid entry, please enter a valid chart ID or dynamic inset URL',
    serverError: 'An error has occurred while trying to fetch the chart data'
  },
  iframe: {
    invalidInput: 'You must enter an iframe URL',
    invalidId: 'Invalid entry, please enter a valid iframe URL',
    serverError: 'An error has occurred while trying to fetch the iframe data'
  },
  url: {
    invalidInput: 'You must enter a URL',
    invalidId: 'Invalid entry, please enter a valid URL',
    serverError: 'An error has occurred while trying to fetch the URL data'
  }
};

export function getImageData(imageData: SnippetyResponseWithImage): SnippetyResponseWithImage {
  const clonedImageData = cloneDeep(imageData);
  let defaultBaseUrl = '';
  try {
    defaultBaseUrl = new URL(clonedImageData.url).origin;
  } catch (error) {
    console.warn(`Found invalid URL ${clonedImageData.url}, when attempting to get image data`);
  }

  return {
    ...clonedImageData.tools.image,
    tools: clonedImageData.tools,
    desc: clonedImageData.desc,
    provider: clonedImageData.provider,
    media: clonedImageData.media,
    href: clonedImageData.tools.image.href,
    src: {
      ...clonedImageData.tools.image.src,
      baseUrl: clonedImageData.tools.image.src?.baseUrl ?? defaultBaseUrl,
      imageId: clonedImageData.tools.image.src?.imageId,
      path: clonedImageData.tools.image.src?.path
    }
  };
}

export function getVideoData(videoData: SnippetyResponseWithVideo): VideoDataForSummarian {
  return {
    alt: videoData.title || '',
    aspectRatio: 1.7778, // Default 16:9 aspect ratio
    author: videoData.author || 'Unknown Author',
    authorName: videoData.author || '',
    caption: videoData.desc || 'No description available.',
    compat: { item: '', meta: '' },
    context: '',
    date: Date.now() / TIMES.ONE_MINUTE, // Current timestamp as default
    dateFormatted: '',
    dateModifiedFormatted: new Date().toLocaleString(), // Current formatted date
    describe: true,
    description: videoData.desc || 'No description available.',
    editLink: '',
    filename: videoData.title || 'Untitled',
    filesizeHumanReadable: 'Unknown',
    filesizeInBytes: 0,
    format: 'standard',
    guid: videoData.id,
    height: 720, // Assuming video dimensions
    icon: '/wp-includes/images/media/default.png', // Default placeholder icon
    menuOrder: 0,
    meta: false,
    mime: 'image/jpeg',
    modified: Date.now() / TIMES.ONE_MINUTE, // Current timestamp
    name: videoData.title || 'Untitled',
    nonces: { delete: '', edit: '', update: '' },
    orientation: 'landscape',
    search: '',
    sizes: {
      full: {
        height: 720,
        orientation: 'landscape',
        url: videoData.media || '',
        width: 1280
      }
    },
    status: 'inherit',
    subtype: 'jpeg',
    title: videoData.title || 'Untitled',
    type: 'image',
    uploadedTo: 0,
    uploadedToLink: '',
    uploadedToTitle: '',
    url: videoData.media || '',
    width: 1280
  };
}

export function getChartData(chartData: SnippetyResponseWithChart): SnippetyChartData {
  return { media: chartData.media, ...chartData.tools.chart };
}
